import React, { useEffect, useState } from 'react';
import { Notification } from '@spark';
import {
    NO_LABELS_MODE_DEFAULT,
    NO_LABELS_MODE_GENERATE_WITH_AI,
    noLabelsModeOptions,
} from '@/features/datasets/utils/hasLabelsHandling';
import { useCostEstimate } from '@/features/billing/hooks/useCostEstimate';
import { formatEstimatedUsd } from '@/features/billing/components/CostEstimateFooter';

export const LABEL_GENERATION_NOTE_PREFIX = 'Note: AI label generation takes extra processing time and incurs additional costs';
export const LABEL_GENERATION_NOTE_SUFFIX =
    'Labels will be created using the prompt you define in the next step ("Model Setup").';

/**
 * The grey Note under the cards (Figma 9966:36609). On SaaS, when the dataset is already known to
 * the platform, the cost of labelling it is appended: "(estimated $0.53 for 1,200 records)".
 */
export function labelGenerationNoteText(estimate) {
    const amount =
        estimate && !estimate.unavailable_reason && Number.isFinite(estimate.records) && estimate.records > 0
            ? ` (estimated ${formatEstimatedUsd(estimate.estimated_usd)} for ${estimate.records.toLocaleString('en-US')} records)`
            : '';
    return `${LABEL_GENERATION_NOTE_PREFIX}${amount}. ${LABEL_GENERATION_NOTE_SUFFIX}`;
}

/**
 * Wizard "Select how to proceed" cards for unlabeled vs generate-with-AI when the has-labels switch
 * is OFF (Figma 9966:36254): two choice cards (title + description) and, while "Generate labels
 * with AI" is selected, a dismissible grey Note about the extra time and cost.
 *
 * ``costEstimateParams`` (``{usecase_id, dataset_name, is_benchmark}``) quotes the labelling cost
 * through ``POST billing/estimate`` on SaaS; ``null`` while the dataset is not uploaded yet.
 */
const NoLabelsModeSection = ({
    value,
    onChange,
    taskType,
    titleSuffix = '',
    costEstimateParams = null,
}) => {
    const options = noLabelsModeOptions(taskType);
    const allowed = options.map((o) => o.value);
    const selected = allowed.includes(value) ? value : NO_LABELS_MODE_DEFAULT;
    const suffix = titleSuffix ? ` ${titleSuffix}` : '';
    const [noteDismissed, setNoteDismissed] = useState(false);
    const generateSelected = selected === NO_LABELS_MODE_GENERATE_WITH_AI;
    const { estimate } = useCostEstimate('label_generation', costEstimateParams, {
        enabled: generateSelected && !noteDismissed,
    });

    useEffect(() => {
        if (!allowed.includes(value)) {
            onChange(NO_LABELS_MODE_DEFAULT);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, taskType]);

    return (
        <div className="cw-dc-field-row">
            <div className="cw-dc-field-row__label">
                <h3 className="cw-dc-section-title">
                    How would you like to proceed?
                    {suffix}
                </h3>
                <p className="cw-dc-field-row__desc">
                    Choose whether to import without responses or generate them with AI after
                    upload.
                </p>
            </div>
            <div className="cw-dc-field-row__controls">
                <div className="cw-dc-no-labels">
                    <p className="cw-dc-no-labels__heading">Select how to proceed</p>
                    <div className="cw-dc-no-labels__cards" role="radiogroup" aria-label="No labels mode">
                        {options.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={selected === opt.value}
                                aria-label={opt.title}
                                aria-describedby={`cw-dc-no-labels-desc-${opt.value}`}
                                className={`cw-dc-no-labels__card${
                                    selected === opt.value ? ' cw-dc-no-labels__card--selected' : ''
                                }`}
                                onClick={() => onChange(opt.value)}
                            >
                                <span className="cw-dc-no-labels__card-title">{opt.title}</span>
                                <span id={`cw-dc-no-labels-desc-${opt.value}`} className="cw-dc-no-labels__card-desc">
                                    {opt.description}
                                </span>
                            </button>
                        ))}
                    </div>
                    {generateSelected && !noteDismissed ? (
                        <Notification
                            className="cw-dc-no-labels__note"
                            feedback="neutral"
                            fill
                            elevated={false}
                            showTitle={false}
                            content={labelGenerationNoteText(estimate)}
                            showButton={false}
                            showCloseButton
                            onCloseClick={() => setNoteDismissed(true)}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default NoLabelsModeSection;
